import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GhostButton, GoldButton } from "@/components/admin/AdminLayout";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { ArrowLeft, Building2, Users, Car, Wallet, ShieldCheck, XCircle, CheckCircle2, Mail, Phone, Hash, User as UserIcon, MapPin, FileText, ScanLine, Upload } from "lucide-react";
import OCRUploadModal from "@/components/OCRUploadModal";

const FLEET_DOCS = [
  { key: "commercial_certificate", label: "Certidão Comercial", targetField: "doc_commercial_certificate" },
  { key: "fleet_contract", label: "Contrato de Adesão (Frota)", targetField: "doc_signed_contract" },
  { key: "legal_rep_cc", label: "CC do Representante Legal", targetField: "doc_legal_rep_cc" },
];

export default function FleetDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ocrOpen, setOcrOpen] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/fleets/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/fleets/${id}`, { status });
      toast.success(t(`status_${status}`) || status);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyFleetOcr = async (target, result) => {
    const fields = result.fields || {};
    const patch = { [`${target.key}_data`]: fields };
    if (target.targetField && result.file_url) patch[target.targetField] = result.file_url;
    // Pre-fill fleet fields when the OCR gives us the info we already have columns for
    if (target.key === "commercial_certificate") {
      if (fields.company_name && !data.fleet.company_name) patch.company_name = fields.company_name;
      if (fields.nif) patch.nif = fields.nif;
    }
    if (target.key === "fleet_contract" && fields.signed) patch.contract_signed = true;
    if (target.key === "legal_rep_cc" && fields.full_name) patch.legal_representative = fields.full_name;
    try {
      await api.patch(`/admin/fleets/${id}`, patch);
      toast.success(`${target.label} extraído ${result.confidence ? `· ${(result.confidence * 100).toFixed(0)}%` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="p-10 text-slate-500">{t("loading")}</div>;
  if (!data) return <div className="p-10 text-slate-500">Sem dados.</div>;

  const { fleet, drivers, vehicles, recent_bookings: bookings, kpis } = data;

  return (
    <div className="p-10 max-w-[1600px]" data-testid="fleet-detail-page">
      <div className="mb-6">
        <Link to="/admin/fleets" className="text-xs uppercase tracking-widest text-slate-600 hover:text-[#f59e0b] inline-flex items-center gap-1 font-bold">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("nav_fleets")}
        </Link>
      </div>

      <PageHeader
        eyebrow={t("fleet")}
        title={fleet.company_name}
        description={`NIF ${fleet.nif} · ${fleet.address || "—"}`}
      >
        <StatBadge status={fleet.status} />
        {fleet.status !== "approved" && (
          <GoldButton onClick={() => setStatus("approved")} data-testid="fleet-approve">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t("approve")}
          </GoldButton>
        )}
        {fleet.status !== "suspended" && (
          <GhostButton onClick={() => setStatus("suspended")}>
            <XCircle className="w-3.5 h-3.5" /> {t("status_suspended")}
          </GhostButton>
        )}
      </PageHeader>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi label={t("nav_drivers")} value={`${kpis.drivers_approved}/${kpis.drivers_count}`} icon={Users} data-testid="kpi-drivers" />
        <Kpi label={t("nav_vehicles")} value={`${kpis.vehicles_available}/${kpis.vehicles_count}`} icon={Car} data-testid="kpi-vehicles" />
        <Kpi label={t("bookings_count")} value={kpis.bookings_count} icon={FileText} data-testid="kpi-bookings" />
        <Kpi label="Comissão recente" value={`€ ${kpis.commission_recent.toFixed(2)}`} icon={Wallet} data-testid="kpi-commission" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Company info + docs */}
        <aside className="lg:col-span-1 space-y-4">
          <Card title="Informação da empresa" icon={Building2}>
            <Row icon={UserIcon} label="Rep. legal" value={fleet.legal_representative} />
            <Row icon={Mail} label="Email" value={fleet.email} />
            <Row icon={Phone} label="Telefone" value={fleet.phone} />
            <Row icon={Hash} label="IBAN" value={fleet.iban} />
            <Row icon={MapPin} label="Morada" value={fleet.address} />
          </Card>

          {(fleet.partners_ids || []).length > 0 && (
            <Card title="Sócios / Partners" icon={ShieldCheck}>
              {fleet.partners_ids.map((p, i) => (
                <div key={i} className="border-b border-slate-100 last:border-0 py-2">
                  <div className="text-sm font-semibold text-[#0a0f1e]">{p.name || "—"}</div>
                  <div className="text-xs text-slate-600 font-mono">{p.document}</div>
                </div>
              ))}
            </Card>
          )}

          <Card title="Documentos · OCR" icon={ScanLine}>
            {FLEET_DOCS.map((doc) => {
              const stored = fleet[`${doc.key}_data`];
              const url = fleet[doc.targetField];
              return (
                <div key={doc.key} className={`border p-2.5 mb-2 last:mb-0 ${stored ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`fleet-doc-${doc.key}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0a0f1e]">{doc.label}</div>
                      {stored ? (
                        <div className="text-[10px] font-mono text-slate-600 mt-1 truncate">
                          {stored.company_name || stored.full_name || stored.fleet_name || "OCR concluído"}
                          {stored.expiry_date && <span className="ml-2">Val: <span className="text-[#0a0f1e] font-bold">{stored.expiry_date}</span></span>}
                        </div>
                      ) : (
                        <div className="text-[10px] text-slate-500 mt-1">Ainda não carregado</div>
                      )}
                      {url && <a href={url} target="_blank" rel="noreferrer" className="text-[10px] text-[#f59e0b] uppercase tracking-widest hover:underline">Ver ficheiro →</a>}
                    </div>
                    <button
                      onClick={() => setOcrOpen(doc)}
                      data-testid={`fleet-ocr-btn-${doc.key}`}
                      className="flex-shrink-0 text-[10px] uppercase tracking-widest font-bold border border-[#f59e0b] text-[#f59e0b] hover:bg-amber-50 px-2.5 py-1.5"
                    >
                      <Upload className="w-3 h-3 inline mr-1" /> {stored ? "Substituir" : "Carregar"}
                    </button>
                  </div>
                </div>
              );
            })}
          </Card>
        </aside>

        {/* Right: Drivers + Vehicles + Bookings */}
        <section className="lg:col-span-2 space-y-6">
          <PanelTable
            title={`${t("nav_drivers")} (${drivers.length})`}
            emptyText="Sem motoristas associados."
            data-testid="fleet-drivers-panel"
            rows={drivers}
            columns={[
              { key: "name", label: t("name"), render: (d) => <Link to={`/admin/drivers/${d.id}`} className="text-[#0a0f1e] font-semibold hover:text-[#f59e0b]" data-testid={`fleet-driver-link-${d.id}`}>{d.name}</Link> },
              { key: "email", label: t("email"), render: (d) => <span className="text-xs text-slate-700">{d.email}</span> },
              { key: "phone", label: t("phone"), render: (d) => <span className="text-xs text-slate-700">{d.phone || "—"}</span> },
              { key: "status", label: t("status"), render: (d) => <StatBadge status={d.status} /> },
              { key: "trips", label: "Viagens", render: (d) => <span className="text-sm font-bold text-[#0a0f1e]">{d.trips_count || 0}</span>, align: "right" },
            ]}
          />
          <PanelTable
            title={`${t("nav_vehicles")} (${vehicles.length})`}
            emptyText="Sem veículos associados."
            data-testid="fleet-vehicles-panel"
            rows={vehicles}
            columns={[
              { key: "plate", label: "Matrícula", render: (v) => <span className="text-[#0a0f1e] font-mono font-bold">{v.plate}</span> },
              { key: "brand", label: t("vehicle"), render: (v) => <span className="text-sm text-slate-700">{v.brand} {v.model}</span> },
              { key: "type", label: "Tipo", render: (v) => <span className="text-xs uppercase text-slate-700 font-semibold">{v.type}</span> },
              { key: "cap", label: "Capacidade", render: (v) => <span className="text-xs text-slate-700">{v.capacity_passengers} pax · {v.capacity_luggage} bag</span> },
              { key: "status", label: t("status"), render: (v) => <StatBadge status={v.status} /> },
            ]}
          />
          <PanelTable
            title={`Reservas recentes (${bookings.length})`}
            emptyText="Sem reservas recentes."
            data-testid="fleet-bookings-panel"
            rows={bookings.slice(0, 20)}
            columns={[
              { key: "ref", label: "Ref.", render: (b) => <Link to={`/admin/bookings/${b.id}`} className="text-[#0a0f1e] font-mono font-semibold hover:text-[#f59e0b]">{b.reference}</Link> },
              { key: "date", label: t("date"), render: (b) => <span className="text-xs text-slate-700">{(b.service_datetime || "").slice(0, 16).replace("T", " ")}</span> },
              { key: "route", label: "Rota", render: (b) => <span className="text-xs text-slate-700 truncate max-w-[220px] block">{b.pickup_address} → {b.destination_address}</span> },
              { key: "status", label: t("status"), render: (b) => <StatBadge status={b.status} /> },
              { key: "amt", label: t("amount"), render: (b) => <span className="text-sm font-bold text-[#0a0f1e]">€ {(b.client_pays ?? b.total_amount ?? 0).toFixed(2)}</span>, align: "right" },
            ]}
          />
        </section>
      </div>

      {ocrOpen && (
        <OCRUploadModal
          documentType={ocrOpen.key}
          label={ocrOpen.label}
          onCancel={() => setOcrOpen(null)}
          onConfirm={async (res) => { await applyFleetOcr(ocrOpen, res); setOcrOpen(null); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, ...props }) {
  return (
    <div className="border border-slate-200 bg-white p-4" {...props}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-700 font-bold">
        <Icon className="w-3.5 h-3.5 text-[#f59e0b]" /> {label}
      </div>
      <div className="mt-2 text-2xl font-serif text-[#0a0f1e] font-bold">{value}</div>
    </div>
  );
}
function Card({ title, icon: Icon, children }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#f59e0b]" />}
        <span className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{title}</span>
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5" />}
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">{label}</div>
        <div className="text-slate-800 mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}
function DocLink({ label, url }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700 font-semibold">{label}</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] uppercase text-[#f59e0b] hover:underline font-bold">Ver →</a>
      ) : (
        <span className="text-[11px] text-slate-500">—</span>
      )}
    </div>
  );
}
function PanelTable({ title, emptyText, rows, columns, ...props }) {
  return (
    <div className="border border-slate-200 bg-white" {...props}>
      <div className="px-5 py-3 border-b border-slate-200">
        <span className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{title}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2 text-[10px] uppercase tracking-widest text-slate-700 font-bold ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-2.5 ${c.align === "right" ? "text-right" : ""}`}>{c.render(r)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
